import { useCallback, useMemo } from "react";
import { useDispatch } from "react-redux";
import { Stack } from "@fluentui/react/lib/Stack";

import { WithConfiguration } from "Components/Generic/Configuration";
import { CustomPanel, CustomPanelSizeType } from "Components/Generic/CustomPanel";
import { IHeaderButtonProps } from "Components/Generic/CustomPanel/Header/Button";
import { WithTheme } from "Components/Generic/Theme/WithTheme";
import { WithTranslator } from "Components/Generic/Translator";
import { IFieldMetadata } from "Model/Configuration/IFieldMetadata";
import { EntityType } from "Model/EntityType";
import { uiSetActiveEditItemId } from "Store/Actions";
import { Configuration } from "Utils/Configuration";
import { Translator } from "Utils/Translator";
import { EditField } from "./EditField";
import { IEditProps } from "./IEditProps";
import { IUseEdit } from "./IUseEdit";
import { useEdit } from "./UseEdit";
import { useEditStyles } from "./UseEditStyles";

export const Edit = WithTheme(
  WithConfiguration(
    WithTranslator((props: IEditProps): JSX.Element => {
      const {
        config,
        translator,
        theme: { spacing }
      } = props;
      const dispatch = useDispatch();
      const useEditProps = useEdit();
      const {
        isOpen, item, type, onChange
      } = useEditProps;
      const styles = useEditStyles();

      const editConfig = useMemo(
        () => getConfigByType(config, type, item?.dataSource as string),
        [config, type, item?.dataSource]
      );
      const onDismiss = useCallback(() => dispatch(uiSetActiveEditItemId(null)), [dispatch]);
      const buttonProps = useMemo(() => getButtonProps(useEditProps, translator), [translator, useEditProps]);

      if (item == null || type === EntityType.None) {
        return null;
      }

      return (
        <CustomPanel
          customPanelSize={CustomPanelSizeType.small}
          isOpen={isOpen}
          onDismiss={onDismiss}
          isLightDismiss
          {...buttonProps}
        >
          <Stack className={styles.root} tokens={{ childrenGap: spacing.l2 }}>
            {editConfig.map((group, i) => (
              // eslint-disable-next-line react/no-array-index-key
              <Stack key={i} tokens={{ childrenGap: spacing.s1 }}>
                {group.map((fieldConfig) => {
                  const { fieldName } = fieldConfig;
                  return (
                    <Stack.Item key={fieldName}>
                      <EditField {...fieldConfig} item={item} onChange={onChange} />
                    </Stack.Item>
                  );
                })}
              </Stack>
            ))}
          </Stack>
        </CustomPanel>
      );
    })
  )
);

function getConfigByType(config: Configuration, type: EntityType, dataSource: string): IFieldMetadata[][] {
  if (type === EntityType.Project) return config.getEditProject(dataSource);
  if (type === EntityType.Company) return config.getEditCompany(dataSource);
  if (type === EntityType.Contact) return config.getEditContact(dataSource);
  return null;
}

function getButtonProps(useEditProps: IUseEdit, translator: Translator): IHeaderButtonProps {
  const { useExport, onSave, onSaveExport } = useEditProps;

  let buttonProps: IHeaderButtonProps = {
    buttonText: translator.get(`generic.saveclose`, `Save & Close`),
    buttonIcon: `SaveAndClose`,
    onButtonClick: onSave
  };

  if (useExport) {
    buttonProps = {
      ...buttonProps,
      splitButtonText: buttonProps.buttonText,
      splitButtonIcon: buttonProps.buttonIcon,
      onSplitButtonClick: buttonProps.onButtonClick,
      buttonText: translator.get(`generic.saveexport`, `Save & Export`),
      onButtonClick: onSaveExport
    };
  }

  return buttonProps;
}
